import React, { useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import { getLocalStorage } from "../../utils/AuthHelpers/AuthCookieHandler";
import { formatPrice, isEmpty } from "../../utils/FrontEndHelpers";
import ContactService from "../../services/ContactService";
import moment from "moment";

const ReceiptPage = ({
  handleNext,
  sessionData,
  zoneObj: { zone, location },
}) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSent, setIsSent] = useState(false);

  const handleSubmit = async () => {
    setIsLoading(true);
    if (isEmpty(email)) {
      setErrorMessage("Please provide a valid email address");
      return;
    }

    if (isEmpty(name)) {
      setErrorMessage("Please provide a valid name");
      return;
    }

    const receiptObject = {
      email,
      name,
      locationName: location.name,
      locationAddress: location.address,
      locationId: sessionData.locationId,
      zoneId: sessionData.zoneId,
      zoneNumber: sessionData.zoneNumber,
      fees: sessionData.fees,
      rate: sessionData.rate,
      totalCost: formatPrice(sessionData.fees + sessionData.rate),
      licensePlate: `${sessionData?.vehiclePlate?.toUpperCase()}, ${sessionData?.vehicleState?.toUpperCase()}`,
    };

    try {
      const contactService = new ContactService();
      await contactService.sendReceipt(receiptObject);
      setIsSent(true);

      // clear the form
      setEmail("");
      setName("");
      setIsLoading(false);
    } catch (err) {
      setErrorMessage(err.message);
    }

    setIsLoading(false);
    handleNext();
  };

  useEffect(() => {
    const user = getLocalStorage("user");
    if (user) {
      setName(user.name);
      setEmail(user.email);
    } else {
      setName("");
      setEmail("");
    }
  }, []);

  return (
    <div style={{ margin: "0 30px" }}>
      <ReceiptContainer>
        <ReceiptEdge />
        <ReceiptHeader>
          <SuccessIcon>✓</SuccessIcon>
          <ReceiptTitle>Thank You!</ReceiptTitle>
          <ReceiptSubtitle>Your Payment Was Successful</ReceiptSubtitle>
          <ReceiptAddress>{location.address}</ReceiptAddress>
        </ReceiptHeader>

        <ReceiptBody>
          <ReceiptRow>
            <ReceiptLabel>Zone</ReceiptLabel>
            <ReceiptValue>#{zone.zoneNumber}</ReceiptValue>
          </ReceiptRow>
          <ReceiptRow>
            <ReceiptLabel>Date</ReceiptLabel>
            <ReceiptValue>{moment().format("MMMM Do, YYYY")}</ReceiptValue>
          </ReceiptRow>
          <ReceiptRow>
            <ReceiptLabel>License Plate</ReceiptLabel>
            <ReceiptValue>
              {sessionData?.vehiclePlate?.toUpperCase()},{" "}
              {sessionData?.vehicleState?.toUpperCase()}
            </ReceiptValue>
          </ReceiptRow>
        </ReceiptBody>

        <ReceiptTotal>
          <span>Total</span>
          <span>
            {formatPrice(
              sessionData?.rate ? sessionData?.fees + sessionData?.rate : 0
            )}
          </span>
        </ReceiptTotal>

        <ReceiptForm onSubmit={handleSubmit}>
          <FormTitle>Email Receipt</FormTitle>
          <FormGroup>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder="Enter your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={isLoading}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
            />
          </FormGroup>

          <Button type="submit" disabled={isLoading} sending={isLoading}>
            {isLoading
              ? "Sending..."
              : isSent
              ? "✓ Receipt Sent"
              : "Send Receipt"}
          </Button>

          {errorMessage && (
            <div
              style={{ color: "red", textAlign: "center", marginTop: "10px" }}
            >
              {errorMessage}
            </div>
          )}
        </ReceiptForm>

        <ReceiptFooter>
          <ThankYouMessage>We appreciate your business!</ThankYouMessage>
          {/* <div>Reference Number: 2025-04-12-684-ATL</div> */}
        </ReceiptFooter>
      </ReceiptContainer>
    </div>
  );
};

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

// Paper texture background
const paperTexture = `
  linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.8) 0%,
    rgba(245, 245, 245, 0.8) 100%
  ),
  repeating-linear-gradient(
    45deg,
    rgba(0, 0, 0, 0.01) 0,
    rgba(0, 0, 0, 0.01) 1px,
    transparent 1px,
    transparent 3px
  )
`;

// Styled Components
const ReceiptContainer = styled.div`
  max-width: 380px;
  margin: 0 auto;
  background: ${paperTexture};
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  padding: 0;
  font-family: "Courier New", monospace;
  color: #333;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease-out;
`;

const ReceiptEdge = styled.div`
  height: 12px;
  background-image: linear-gradient(
    90deg,
    transparent 0%,
    transparent 50%,
    #fff 50%,
    #fff 100%
  );
  background-size: 20px 100%;
  width: 100%;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: #ddd;
  }
`;

const ReceiptHeader = styled.div`
  text-align: center;
  padding: 20px 20px 10px;
  border-bottom: 1px dashed #ccc;
`;

const ReceiptTitle = styled.h1`
  font-size: 28px;
  margin: 0 0 5px;
  font-weight: 700;
  letter-spacing: 1px;
`;

const ReceiptSubtitle = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
`;

const ReceiptAddress = styled.div`
  font-size: 12px;
  color: #777;
  margin-top: 10px;
  line-height: 1.4;
`;

const ReceiptBody = styled.div`
  padding: 15px 20px;
`;

const ReceiptRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px dotted #ddd;

  &:last-child {
    border-bottom: none;
  }
`;

const ReceiptLabel = styled.div`
  font-size: 14px;
  color: #555;
`;

const ReceiptValue = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const ReceiptTotal = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 20px;
  background-color: rgba(0, 0, 0, 0.03);
  border-top: 1px dashed #ccc;
  border-bottom: 1px dashed #ccc;
  font-weight: 700;
  font-size: 16px;
`;

const ReceiptForm = styled.form`
  padding: 20px;
`;

const FormTitle = styled.h3`
  font-size: 16px;
  margin: 0 0 15px;
  text-align: center;
  font-weight: 600;
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  margin-bottom: 5px;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: "Courier New", monospace;
  font-size: 14px;
  background-color: rgba(255, 255, 255, 0.8);

  &:focus {
    outline: none;
    border-color: #888;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 10px;
  background-color: #333;
  color: white;
  border: none;
  border-radius: 4px;
  font-family: "Courier New", monospace;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background-color: #444;
  }

  &:disabled {
    background-color: #999;
    cursor: not-allowed;
  }

  ${(props) =>
    props.sending &&
    css`
      &::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-image: linear-gradient(
          90deg,
          rgba(255, 255, 255, 0) 0,
          rgba(255, 255, 255, 0.2) 20%,
          rgba(255, 255, 255, 0.5) 60%,
          rgba(255, 255, 255, 0)
        );
        animation: ${shimmer} 2s infinite;
        transform: translateX(-100%);
      }
    `}
`;

const ReceiptFooter = styled.div`
  padding: 15px 20px 20px;
  text-align: center;
  font-size: 12px;
  color: #777;
  border-top: 1px dashed #ccc;
`;

const ThankYouMessage = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin: 10px 0;
`;

const SuccessIcon = styled.div`
  width: 50px;
  height: 50px;
  margin: 0 auto 15px;
  border-radius: 50%;
  background-color: #12b981;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
`;

export default ReceiptPage;
